import type { TranslationKeys as TranslationKeysPype } from '../generated/i18nTypes-pype';
import type { TranslationKeys as TranslationKeysProStudio } from '../generated/i18nTypes-pro-studio';
import type { TranslationKeys as TranslationKeysManager } from '../generated/i18nTypes-manager';
import type { TranslationKeys as TranslationKeysNavigation } from '../generated/i18nTypes-navigation';
import type { TranslationKeys as TranslationKeysDesignSystem } from '../generated/i18nTypes-designSystem';

/**
 * The languages the Pype widget ships, generated from the `locales/pype` subdirectories.
 *
 * Re-exported here so `@pypestream/translations/types` stays the single public surface for language
 * typing — consumers must not reach into `generated/`. Scope-specific by design: it is NOT
 * interchangeable with `LanguageCodes` / `AvailableLanguages` below, which are shared across all six
 * scopes and feed `supportedLngs` for every one of them. Pype ships a different set (no `hi`).
 */
export type { PypeLanguage } from '../generated/pype-languages';
export { PYPE_LANGUAGE_CODES } from '../generated/pype-languages';

export enum LanguageScopes {
  proStudio = 'pro-studio',
  pype = 'pype',
  manager = 'manager',
  navigation = 'navigation',
  designSystem = 'designSystem',
}

type Dates =
  | 'dates:fullDate'
  | 'dates:localisedDate'
  | 'dates:weekDay'
  | 'dates:postedOn';

export type TranslationKeys =
  | TranslationKeysPype
  | TranslationKeysProStudio
  | TranslationKeysManager
  | TranslationKeysNavigation
  | TranslationKeysDesignSystem
  | Dates;

export const LanguageCodes = {
  en: 'en',
  zh: 'zh',
  hi: 'hi',
  ar: 'ar',
  ru: 'ru',
  bg: 'bg',
  bn: 'bn',
  cs: 'cs',
  da: 'da',
  de: 'de',
  el: 'el',
  'en-AU': 'en-AU',
  'en-GB': 'en-GB',
  es: 'es',
  et: 'et',
  fi: 'fi',
  fr: 'fr',
  he: 'he',
  hr: 'hr',
  ht: 'ht',
  hu: 'hu',
  id: 'id',
  is: 'is',
  it: 'it',
  ja: 'ja',
  ko: 'ko',
  lt: 'lt',
  lv: 'lv',
  mk: 'mk',
  ms: 'ms',
  nl: 'nl',
  no: 'no',
  pl: 'pl',
  pt: 'pt',
  ro: 'ro',
  sk: 'sk',
  sl: 'sl',
  sr: 'sr',
  'sr-CYRL': 'sr-CYRL',
  sv: 'sv',
  th: 'th',
  tl: 'tl',
  tr: 'tr',
  uk: 'uk',
  ur: 'ur',
  vi: 'vi',
  yi: 'yi',
  'zh-HK': 'zh-HK',
  'zh-TW': 'zh-TW',
} as const;

export type Language = {
  code: keyof typeof LanguageCodes;
  nativeName?: string;
  /**
   * Added this property to load dateFns locales dynamically.
   * Something like,
   * `../../../node_modules/date-fns/esm/locale/${selectLanguage?.dateFnsCode || 'en-US'}/index.js`
   * @TODO - Need to fix dynamic loading of date-fns locale
   */
  dateFnsCode?: string;
};

// equivalent to i18next's `i18n.dir()` return type; inlined so this module stays free of a
// runtime dependency on ./i18n (which pulls react-i18next). See translation-mixin in the DS.
export type DirType = 'ltr' | 'rtl';

export const AvailableLanguages: Language[] = [
  {
    code: LanguageCodes.en,
    nativeName: 'English',
    dateFnsCode: 'en-US',
  },
  {
    code: LanguageCodes.zh,
    nativeName: 'Chinese',
    dateFnsCode: 'zh-CN',
  },
  {
    code: LanguageCodes.hi,
    nativeName: 'Hindi',
    dateFnsCode: 'hi',
  },
  {
    code: LanguageCodes.ar,
    nativeName: 'Arabic',
    dateFnsCode: 'ar',
  },
  {
    code: LanguageCodes.ru,
    nativeName: 'Russian',
    dateFnsCode: 'ru',
  },
  {
    code: LanguageCodes.bg,
    nativeName: 'Български',
    dateFnsCode: 'bg',
  },
  {
    code: LanguageCodes.bn,
    nativeName: 'বাংলা',
    dateFnsCode: 'bn',
  },
  {
    code: LanguageCodes.cs,
    nativeName: 'Čeština',
    dateFnsCode: 'cs',
  },
  {
    code: LanguageCodes.da,
    nativeName: 'Dansk',
    dateFnsCode: 'da',
  },
  {
    code: LanguageCodes.de,
    nativeName: 'Deutsch',
    dateFnsCode: 'de',
  },
  {
    code: LanguageCodes.el,
    nativeName: 'Ελληνικά',
    dateFnsCode: 'el',
  },
  {
    code: LanguageCodes['en-AU'],
    nativeName: 'English (Australia)',
    dateFnsCode: 'en-AU',
  },
  {
    code: LanguageCodes['en-GB'],
    nativeName: 'English (UK)',
    dateFnsCode: 'en-GB',
  },
  {
    code: LanguageCodes.es,
    nativeName: 'Español',
    dateFnsCode: 'es',
  },
  {
    code: LanguageCodes.et,
    nativeName: 'Eesti',
    dateFnsCode: 'et',
  },
  {
    code: LanguageCodes.fi,
    nativeName: 'Suomi',
    dateFnsCode: 'fi',
  },
  {
    code: LanguageCodes.fr,
    nativeName: 'Français',
    dateFnsCode: 'fr',
  },
  {
    code: LanguageCodes.he,
    nativeName: 'עברית',
    dateFnsCode: 'he',
  },
  {
    code: LanguageCodes.hr,
    nativeName: 'Hrvatski',
    dateFnsCode: 'hr',
  },
  {
    code: LanguageCodes.ht,
    nativeName: 'Kreyòl ayisyen',
    dateFnsCode: 'ht',
  },
  {
    code: LanguageCodes.hu,
    nativeName: 'Magyar',
    dateFnsCode: 'hu',
  },
  {
    code: LanguageCodes.id,
    nativeName: 'Bahasa Indonesia',
    dateFnsCode: 'id',
  },
  {
    code: LanguageCodes.is,
    nativeName: 'Íslenska',
    dateFnsCode: 'is',
  },
  {
    code: LanguageCodes.it,
    nativeName: 'Italiano',
    dateFnsCode: 'it',
  },
  {
    code: LanguageCodes.ja,
    nativeName: '日本語',
    dateFnsCode: 'ja',
  },
  {
    code: LanguageCodes.ko,
    nativeName: '한국어',
    dateFnsCode: 'ko',
  },
  {
    code: LanguageCodes.lt,
    nativeName: 'Lietuvių',
    dateFnsCode: 'lt',
  },
  {
    code: LanguageCodes.lv,
    nativeName: 'Latviešu',
    dateFnsCode: 'lv',
  },
  {
    code: LanguageCodes.mk,
    nativeName: 'Македонски',
    dateFnsCode: 'mk',
  },
  {
    code: LanguageCodes.ms,
    nativeName: 'Bahasa Melayu',
    dateFnsCode: 'ms',
  },
  {
    code: LanguageCodes.nl,
    nativeName: 'Nederlands',
    dateFnsCode: 'nl',
  },
  {
    code: LanguageCodes.no,
    nativeName: 'Norsk',
    dateFnsCode: 'nb',
  },
  {
    code: LanguageCodes.pl,
    nativeName: 'Polski',
    dateFnsCode: 'pl',
  },
  {
    code: LanguageCodes.pt,
    nativeName: 'Português',
    dateFnsCode: 'pt',
  },
  {
    code: LanguageCodes.ro,
    nativeName: 'Română',
    dateFnsCode: 'ro',
  },
  {
    code: LanguageCodes.sk,
    nativeName: 'Slovenčina',
    dateFnsCode: 'sk',
  },
  {
    code: LanguageCodes.sl,
    nativeName: 'Slovenščina',
    dateFnsCode: 'sl',
  },
  {
    code: LanguageCodes.sr,
    nativeName: 'Srpski',
    dateFnsCode: 'sr-Latn',
  },
  {
    code: LanguageCodes['sr-CYRL'],
    nativeName: 'Српски',
    dateFnsCode: 'sr',
  },
  {
    code: LanguageCodes.sv,
    nativeName: 'Svenska',
    dateFnsCode: 'sv',
  },
  {
    code: LanguageCodes.th,
    nativeName: 'ไทย',
    dateFnsCode: 'th',
  },
  {
    code: LanguageCodes.tl,
    nativeName: 'Tagalog',
    dateFnsCode: 'en-US',
  },
  {
    code: LanguageCodes.tr,
    nativeName: 'Türkçe',
    dateFnsCode: 'tr',
  },
  {
    code: LanguageCodes.uk,
    nativeName: 'Українська',
    dateFnsCode: 'uk',
  },
  {
    code: LanguageCodes.ur,
    nativeName: 'اردو',
    dateFnsCode: 'en-US',
  },
  {
    code: LanguageCodes.vi,
    nativeName: 'Tiếng Việt',
    dateFnsCode: 'vi',
  },
  {
    code: LanguageCodes.yi,
    nativeName: 'ייִדיש',
    dateFnsCode: 'en-US',
  },
  {
    code: LanguageCodes['zh-HK'],
    nativeName: '繁體中文（香港）',
    dateFnsCode: 'zh-HK',
  },
  {
    code: LanguageCodes['zh-TW'],
    nativeName: '繁體中文（台灣）',
    dateFnsCode: 'zh-TW',
  },
];

export type TransKeyProp = {
  i18nKey?: TranslationKeys;
};

interface Translations {
  [key: string]: string | Record<string, string>;
}

export interface TranslationData {
  lang: string;
  file: string;
  namespace: string;
  translations: Translations;
}
